/**
 * Admin releases component.
 *
 * Displays release management UI with freeze status, filters, and table.
 * Data fetching and mutations handled by runtime via callbacks.
 */

import * as stylex from "@stylexjs/stylex";
import { useState } from "react";

import { alert, button, modal, spacing, table, text } from "@animus/primaris";

import type { FreezeStatusMap, Release, ReleasePlatform } from "./types.ts";
import { PinConfirmationModal } from "./pin-confirmation-modal.tsx";
import { ReleaseRow } from "./release-row.tsx";
import { styles } from "./styles.stylex.ts";

type FilterPlatform = ReleasePlatform | "all";

interface AdminReleasesProps {
  releases: Release[];
  freezeStatus: FreezeStatusMap | null;
  isLoading?: boolean;
  error?: string | null;
  isPinning?: boolean;
  isUnpinning?: boolean;
  onRefresh: () => void;
  onFilterChange: (filter: FilterPlatform) => void;
  onPin: (
    platform: ReleasePlatform,
    gitSha: string,
    reason?: string
  ) => Promise<void>;
  onUnpin: (platform: ReleasePlatform) => Promise<void>;
}

function AdminReleases({
  releases,
  freezeStatus,
  isLoading,
  error,
  isPinning,
  isUnpinning,
  onRefresh,
  onFilterChange,
  onPin,
  onUnpin,
}: AdminReleasesProps) {
  const [filter, setFilter] = useState<FilterPlatform>("all");
  const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [unpinPlatform, setUnpinPlatform] = useState<ReleasePlatform | null>(
    null
  );

  function handleFilterChange(newFilter: FilterPlatform) {
    setFilter(newFilter);
    onFilterChange(newFilter);
  }

  function handlePin(release: Release) {
    setSelectedRelease(release);
    setActionError(null);
  }

  async function handleConfirmPin(
    reason: string,
    _acknowledgeIncompatibility: boolean
  ) {
    if (!selectedRelease) {
      return;
    }

    try {
      await onPin(
        selectedRelease.platform,
        selectedRelease.gitSha,
        reason || undefined
      );
      setSelectedRelease(null);
      setActionError(null);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to pin release");
    }
  }

  function handleUnpin(platform: ReleasePlatform) {
    setUnpinPlatform(platform);
    setActionError(null);
  }

  async function handleConfirmUnpin() {
    if (!unpinPlatform) {
      return;
    }

    try {
      await onUnpin(unpinPlatform);
      setUnpinPlatform(null);
      setActionError(null);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to unpin");
    }
  }

  function isPinnedRelease(release: Release): boolean {
    const freeze = freezeStatus?.[release.platform];
    return freeze?.isFrozen === true && freeze.pinnedVersion === release.gitSha;
  }

  return (
    <div>
      <div {...stylex.props(styles.header)}>
        <h1 {...stylex.props(text.bold, text["2xl"], text.white)}>
          Release Management
        </h1>
        <button
          {...stylex.props(button.base, button.secondary, button.small)}
          disabled={isLoading}
          onClick={onRefresh}
          type="button"
        >
          Refresh
        </button>
      </div>

      {/* Freeze Status Cards */}
      <div {...stylex.props(styles.statusGrid)}>
        {(["web", "marketing", "mobile"] as const).map((platform) => {
          const freeze = freezeStatus?.[platform];
          return (
            <div
              {...stylex.props(
                styles.statusCard,
                freeze?.isFrozen
                  ? styles.statusCardPinned
                  : styles.statusCardNormal
              )}
              key={platform}
            >
              <div
                {...stylex.props(
                  spacing.mb1,
                  text.medium,
                  text.white,
                  text.capitalize
                )}
              >
                {platform}
              </div>
              {freeze?.isFrozen ? (
                <>
                  <div {...stylex.props(styles.pinnedText)}>
                    Pinned at {freeze.pinnedVersion?.slice(0, 7)}
                  </div>
                  {freeze.reason && (
                    <div {...stylex.props(text.xs, text.muted)}>
                      {freeze.reason}
                    </div>
                  )}
                </>
              ) : (
                <div {...stylex.props(text.sm, text.muted)}>Serving latest</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Filter */}
      <div {...stylex.props(styles.filterRow)}>
        {(["all", "web", "marketing", "mobile"] as const).map((p) => (
          <button
            {...stylex.props(
              styles.filterButton,
              filter === p ? styles.filterActive : styles.filterInactive
            )}
            key={p}
            onClick={() => handleFilterChange(p)}
            type="button"
          >
            {p}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && <div {...stylex.props(spacing.mb4, alert.error)}>{error}</div>}

      {/* Loading */}
      {isLoading && (
        <div {...stylex.props(styles.loading)}>Loading releases...</div>
      )}

      {/* Release Table */}
      {!isLoading && (
        <div {...stylex.props(styles.tableWrapper)}>
          <table {...stylex.props(table.table)}>
            <thead {...stylex.props(table.thead)}>
              <tr>
                <th {...stylex.props(table.th)}>Platform</th>
                <th {...stylex.props(table.th)}>SHA</th>
                <th {...stylex.props(table.th)}>Message</th>
                <th {...stylex.props(table.th)}>Author</th>
                <th {...stylex.props(table.th)}>Date</th>
                <th {...stylex.props(table.th, table.thRight)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {releases.map((release) => (
                <ReleaseRow
                  isPinned={isPinnedRelease(release)}
                  key={`${release.platform}-${release.id}`}
                  onPin={handlePin}
                  onUnpin={handleUnpin}
                  release={release}
                />
              ))}
            </tbody>
          </table>

          {releases.length === 0 && (
            <div {...stylex.props(styles.emptyState)}>No releases found</div>
          )}
        </div>
      )}

      {/* Pin Confirmation Modal */}
      {selectedRelease && (
        <PinConfirmationModal
          error={actionError}
          isLoading={isPinning ?? false}
          onCancel={() => setSelectedRelease(null)}
          onConfirm={handleConfirmPin}
          release={selectedRelease}
        />
      )}

      {/* Unpin Confirmation Modal */}
      {unpinPlatform && (
        <div {...stylex.props(modal.overlay)}>
          <div {...stylex.props(styles.unpinModal)}>
            <h2
              {...stylex.props(spacing.mb4, text.semibold, text.lg, text.white)}
            >
              Unpin {unpinPlatform}?
            </h2>
            <p {...stylex.props(spacing.mb4, text.muted)}>
              This will allow new deployments to be served immediately.
            </p>
            {actionError && (
              <div {...stylex.props(spacing.mb4, alert.error, text.sm)}>
                {actionError}
              </div>
            )}
            <div {...stylex.props(modal.footer)}>
              <button
                {...stylex.props(button.base, button.secondary)}
                onClick={() => setUnpinPlatform(null)}
                type="button"
              >
                Cancel
              </button>
              <button
                {...stylex.props(button.base, button.primary)}
                disabled={isUnpinning}
                onClick={handleConfirmUnpin}
                type="button"
              >
                {isUnpinning ? "Unpinning..." : "Unpin"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { AdminReleases, type AdminReleasesProps };
